// components/InviteUserForm.tsx
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { v4 as uuidv4 } from "uuid";

import { UserRole, User } from "@/types";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { userApi, InviteRequest, InviteResponse } from "@/services/userService";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Roles } from "@/enums/roles.enum";

const inviteFormSchema = z.object({
  email: z.string().email({ message: "Please enter a valid email address" }),
  role: z.enum(
    [Roles.ADMIN, Roles.VENDOR, Roles.CLIENT, Roles.CONTRACTOR, Roles.MEMBER] as const,
    { required_error: "Please select a role" }
  ),
});
type InviteFormValues = z.infer<typeof inviteFormSchema>;

export default function InviteUserForm() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const form = useForm<InviteFormValues>({
    resolver: zodResolver(inviteFormSchema),
    defaultValues: { email: "", role: Roles.CLIENT },
  });

  // figure out available role
  const availableRoles: UserRole[] = (() => {
    if (!user) return [];
    if (user.role === Roles.ADMIN) {
      return [Roles.ADMIN, Roles.VENDOR, Roles.CLIENT, Roles.CONTRACTOR, Roles.MEMBER];
    }
    if (user.role === Roles.VENDOR) {
      return [Roles.CLIENT, Roles.CONTRACTOR];
    }
    return [];
  })();

  // React Query mutation
  const mutation = useMutation<InviteResponse, Error, InviteRequest>({
    mutationFn: (data) => userApi.inviteUser(data),
    onSuccess: (data, variables) => {
      toast({
        title: "Invitation sent",
        description: `An invitation has been sent to ${variables.email}`,
      });
      form.reset();
      setOpen(false);
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (err) => {
      toast({
        title: "Error sending invite",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  // no perms → nothing
  if (availableRoles.length === 0) {
    return null;
  }

  const onSubmit = (vals: InviteFormValues) => {
    mutation.mutate({ email: vals.email, role: vals.role });
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button>Invite User</Button>
      </SheetTrigger>
      <SheetContent className="sm:max-w-[425px]">
        <SheetHeader>
          <SheetTitle>Invite New User</SheetTitle>
          <SheetDescription>
            Send an invitation to a new user. They will receive an email with
            instructions to join.
          </SheetDescription>
        </SheetHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4 py-4"
          >
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input placeholder="user@example.com" {...field} />
                  </FormControl>
                  <FormDescription>
                    This email will receive the invitation.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Role</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a role" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {availableRoles.map((role) => (
                        <SelectItem key={role} value={role}>
                          {role.charAt(0).toUpperCase() + role.slice(1)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    This determines what permissions the user will have.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>

        <SheetFooter>
          <Button
            onClick={form.handleSubmit(onSubmit)}
            disabled={mutation.isPending}
          >
            {mutation.isPending ? "Sending..." : "Send Invitation"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
